import { useRef, type ReactNode } from 'react';
import { OverlayScrollArea } from '@odo/shared-ui';
import { TAB_KEY_ATTRIBUTE, useTabStripScroll } from './useTabStripScroll';

// w-6 below: the reveal scroll clears the fade so the active tab is never half-faded.
const FADE_WIDTH_PX = 24;
const FADE_CLASS =
    'pointer-events-none absolute inset-y-0 w-6 from-white to-transparent transition-opacity duration-200';

interface TaskTabBarTab {
    key: string;
    label: ReactNode;
    /** Sits in a group pushed to the bar's right end, after a divider. */
    trailing?: boolean;
}

interface TaskTabBarProps {
    tabs: readonly TaskTabBarTab[];
    activeKey: string;
    onSelect: (key: string) => void;
}

function EdgeFade({ side, visible }: { side: 'start' | 'end'; visible: boolean }) {
    const placement = side === 'start' ? 'left-0 bg-gradient-to-r' : 'right-0 bg-gradient-to-l';
    return (
        <div
            aria-hidden="true"
            className={`${FADE_CLASS} ${placement} ${visible ? 'opacity-100' : 'opacity-0'}`}
        />
    );
}

// Scrolls sideways when the labels outgrow the panel, fading the ends that hide tabs; when they
// fit it lays out as a plain row.
export function TaskTabBar({ tabs, activeKey, onSelect }: TaskTabBarProps) {
    const stripRef = useRef<HTMLDivElement | null>(null);
    const hidden = useTabStripScroll(stripRef, activeKey, FADE_WIDTH_PX);
    const leading = tabs.filter((t) => !t.trailing);
    const trailing = tabs.filter((t) => t.trailing);
    const renderTab = (t: TaskTabBarTab) => {
        const active = t.key === activeKey;
        return (
            <button
                key={t.key}
                type="button"
                {...{ [TAB_KEY_ATTRIBUTE]: t.key }}
                onClick={() => onSelect(t.key)}
                className={`flex-shrink-0 whitespace-nowrap px-2.5 py-1 rounded-md text-[11px] font-semibold transition-colors ${
                    active ? 'bg-gray-900 text-white' : 'text-gray-600 hover:bg-gray-100'
                }`}
            >
                {t.label}
            </button>
        );
    };
    return (
        <div className="relative flex-shrink-0 border-b border-gray-200">
            <OverlayScrollArea
                axis="x"
                wheelToHorizontal
                scrollRef={stripRef}
                contentClassName="py-1.5"
                innerClassName="flex items-center gap-1 px-2"
            >
                {leading.map(renderTab)}
                {trailing.length > 0 && (
                    <div className="ml-auto flex items-center gap-1">
                        {leading.length > 0 && (
                            <div aria-hidden="true" className="w-px h-4 bg-gray-200 mx-1.5" />
                        )}
                        {trailing.map(renderTab)}
                    </div>
                )}
            </OverlayScrollArea>
            <EdgeFade side="start" visible={hidden.start} />
            <EdgeFade side="end" visible={hidden.end} />
        </div>
    );
}
